import { router } from "@inertiajs/react";

/**
 * The selectors the tenant's variables are declared on. Doubled `:root` on
 * purpose — `resources/css/app.css` declares the same custom properties on a
 * plain `:root`/`.dark`, and its compiled stylesheet is loaded after this tag,
 * so at equal specificity the platform palette would win and the tenant's
 * branding would never reach the screen.
 *
 * Mirrors `ThemeOverrides::LIGHT_SELECTOR` / `::DARK_SELECTOR` in
 * `app/Data/Theme/ThemeOverrides.php`, which writes the server-rendered tag
 * this one rewrites. Keep the two in step.
 */
const LIGHT_SELECTOR = ":root:root";
const DARK_SELECTOR = ":root:root.dark, :root:root .dark";

interface ThemeOverridesMap {
    light: Record<string, string>;
    dark: Record<string, string>;
}

function block(selector: string, vars: Record<string, string>): string {
    let css = `${selector} {`;
    for (const [prop, value] of Object.entries(vars)) {
        css += ` ${prop}: ${value};`;
    }

    return css + " }";
}

function applyThemeOverrides(overrides: ThemeOverridesMap | undefined | null): void {
    const id = "tenant-theme";
    let style = document.getElementById(id) as HTMLStyleElement | null;

    const lightVars = overrides?.light ?? {};
    const darkVars = overrides?.dark ?? {};

    const hasOverrides =
        Object.keys(lightVars).length > 0 ||
        Object.keys(darkVars).length > 0;

    if (!hasOverrides) {
        style?.remove();
        return;
    }

    if (!style) {
        style = document.createElement("style");
        style.id = id;
        document.head.appendChild(style);
    }

    let css = "";

    if (Object.keys(lightVars).length > 0) {
        css += block(LIGHT_SELECTOR, lightVars);
    }

    if (Object.keys(darkVars).length > 0) {
        css += " " + block(DARK_SELECTOR, darkVars);
    }

    style.textContent = css;
}

function applyFromPage(props: Record<string, unknown>): void {
    applyThemeOverrides(props.themeOverrides as ThemeOverridesMap | undefined);
}

/**
 * `navigate` covers the first page load and back/forward, but Inertia skips it
 * for a visit that lands on the URL already shown — it becomes a history
 * *replace*. Switching tenant from the dashboard (the switch redirects there)
 * and saving the branding form (it redirects back) are both such visits, so
 * `success`, which fires after every server response, re-applies as well.
 */
export function initThemeInjector(): void {
    router.on("navigate", (event) => {
        applyFromPage(event.detail.page.props as Record<string, unknown>);
    });
    router.on("success", (event) => {
        applyFromPage(event.detail.page.props as Record<string, unknown>);
    });
}
